import type { FragmentRun, PaletteBlock } from "@norkron/web-docz";
import { encodePlInput, type PageConfig } from "@norkron/web-page-layout";
import type { DoczTransportBlock } from "./doczHandle";

/**
 * `lib/brandedPreview` — the pure (React-free) geometry + drawing helpers behind
 * the in-page BRANDED canvas preview (`<BrandedCanvasPreview>`), the replacement
 * for the old `<iframe src=pdf>` preview.
 *
 * ARCHITECTURE (RULE ZERO). The BODY is laid out by the SAME Zig→WASM engine that
 * emits the branded PDF: `@norkron/web-page-layout` runs `pdfz-layout.wasm`
 * (pdfz `flex.Page` → `layoutToSoa`) over the docz block-DTO wire
 * (`@norkron/web-docz` `encodeBlockList`) with the Cerebr8 Body page-master
 * geometry ({@link BODY_PAGE_CONFIG}). Positions are pdfz's — the browser only
 * rasterizes glyphs — so the body reads as the export body, on canvas, in EVERY
 * browser regardless of its PDF-viewer setting.
 *
 * The Cerebr8 BRANDING CHROME — the cover wordmark + title/subtitle, and the
 * per-page running header (mark + title) + footer (page number) — is applied by
 * `src/render/cerebr8_render.zig` + platez at PDF time via the plate spec
 * (`src/render/cerebr8.plate.zon`). The SoA/canvas layout wire is BODY-ONLY by
 * design (a `PageConfig` header + a flat block list — it models no cover section
 * and no running elements), so the plate chrome CANNOT flow through it. This
 * module reproduces that chrome on the canvas from the SAME plate geometry +
 * the SAME asset bytes the server embeds (`/branding/*.png`, copied verbatim
 * from `src/render/assets/cerebr8-branding/`). Every geometry constant below
 * cites its `cerebr8.plate.zon` source so the two stay legible together.
 *
 * FIDELITY (honest). The body BLOCKS are now the SERVER's render-ready branded
 * body — `GET /api/documents/:id/branded-docz` returns the docz block-DTO wire
 * AFTER `html_blocks.explodeRawHtml` + `applyCerebr8Styles` (the EXACT body IR
 * `cerebr8_render` hands pdfz for the PDF). So embedded raw-HTML tables/lists
 * render as REAL typed blocks (not literal `<table>` source), and pagination
 * tracks the PDF far more closely. The body font, margins, and layout engine are
 * byte-identical to the export.
 *
 * Heading fidelity is now CLOSED via the extended PLCF wire: the block-DTO still
 * carries no named-style ref, but {@link BODY_PAGE_CONFIG} threads the plate's
 * per-level heading sizes (20/16/14) + navy (#05002B) into the V1 `pl_layout`
 * header (`@norkron/web-page-layout` `encodePlInput` → pdfz `layout_pipeline`
 * `PageConfig.heading_sizes_pt`/`heading_color` → `RenderOptions`), so the wasm
 * lays out + colours headings exactly as the branded PDF instead of falling back
 * to its 24/18/14 black defaults. The one residual: the chrome (cover + running
 * header/footer) is a faithful client reproduction of the plate geometry, not the
 * server-rendered plate, so sub-pixel cover/header/footer positions may differ.
 */

// ── Cerebr8 palette (cerebr8.plate.zon `.theme.colors`) ──────────────────────
export const CEREBR8_NAVY = "#05002B"; // heading / cover title / running-header title
export const CEREBR8_GREY = "#47445A"; // body text / footer page number / cover footer

// ── page geometry (cerebr8.plate.zon `.page_masters.Body`, A4) ───────────────
export const PAGE_W_PT = 595;
export const PAGE_H_PT = 842;
const MARGIN_TOP_PT = 99; // room for the running header band
const MARGIN_RIGHT_PT = 72;
const MARGIN_BOTTOM_PT = 72;
const MARGIN_LEFT_PT = 72;

/**
 * The `pl_layout` PageConfig for the Cerebr8 BODY pages. Margins mirror the plate
 * `Body` page-master. `justify: false` matches the shipped export, which leaves
 * paragraphs at their DEFAULT ragged-right alignment (see
 * `src/tests/e2e_export_layout_geometry.zig` — "does NOT justify"); forcing
 * justify would also trip pdfz's KP measure-vs-shape metric mismatch. Read-only
 * rendering needs no `(block_ord, byte)` provenance, so the non-justify path is
 * correct here.
 */
export const BODY_PAGE_CONFIG: PageConfig = {
  pageWidthPt: PAGE_W_PT,
  pageHeightPt: PAGE_H_PT,
  marginTopPt: MARGIN_TOP_PT,
  marginRightPt: MARGIN_RIGHT_PT,
  marginBottomPt: MARGIN_BOTTOM_PT,
  marginLeftPt: MARGIN_LEFT_PT,
  fontSetId: 0,
  justify: false,
  hyphenate: false,
  glyphGranular: true,
  // Real glyph-advance ragged wrap — the branded PDF export sets
  // `RenderOptions.measured_greedy = true` because Alexandria is PROPORTIONAL;
  // without it the canvas body wraps on the `measure("M")` count budget and runs
  // ~1 page longer than the PDF (the last FAQ lines spill onto an extra page). This
  // aligns canvas body pagination with the export. `justify:false` keeps the
  // shipped ragged body, so this is the wrap metric that governs it.
  measuredGreedy: true,
  // Body/heading VERTICAL RHYTHM parity with the branded PDF. The block-DTO drops
  // the resolved plate metrics, so without these the wasm falls to its flex
  // defaults (body leading 13, no heading rhythm) and packs ~1 page DENSER than the
  // PDF. Supplying the plate's resolved values makes the canvas body pass
  // geometrically IDENTICAL to the export (same 451x671 rect + body size 10 +
  // these), so pagination matches deterministically instead of oscillating:
  //   - Body leading 14 (`cerebr8.plate.zon` `.Body.leading`; flex default is 13).
  //   - Heading space_before/after (plate H1 22/8, H2 18/6, H3 14/4; >=3 -> H3).
  bodyLeadingPt: 14,
  headingSpaceBeforePt: [22, 18, 14, 14, 14, 14],
  headingSpaceAfterPt: [8, 6, 4, 4, 4, 4],
  // Plate heading metrics (`cerebr8.plate.zon` H1/H2/H3 = 20/16/14pt; levels ≥3
  // collapse to H3, mirroring `cerebr8_render.headingStyleName`) + navy #05002B.
  // These flow through the extended PLCF wire (V1 header) into the wasm layout so
  // the canvas headings render at the SAME sizes + colour as the branded PDF —
  // closing the last preview-fidelity gap. The block-DTO drops the named-style
  // ref, so without this the wasm falls back to its 24/18/14 black defaults.
  headingSizesPt: [20, 16, 14, 14, 14, 14],
  headingColor: CEREBR8_NAVY,
};

/** The cover subtitle the branded export uses when the caller supplies none —
 *  `cerebr8_render.default_subtitle`. Kept in sync so the canvas cover matches
 *  the PDF cover for the default (only) export path. */
export const DEFAULT_SUBTITLE = "Editorial draft for review";

/** Public asset paths (served from `web/public/branding/`, copied verbatim from
 *  the Zig-embedded `src/render/assets/cerebr8-branding/`). */
export const COVER_WORDMARK_SRC = "/branding/cerebr8-cover-wordmark.png";
export const HEADER_MARK_SRC = "/branding/cerebr8-header-mark.png";

// ── the `pl_layout` input wire for the branded BODY ──────────────────────────
/**
 * Frame the `pl_layout` input for the branded BODY: prepend the Cerebr8 Body
 * PageConfig header ({@link BODY_PAGE_CONFIG}) onto the SERVER's render-ready docz
 * block-DTO list bytes (`GET /api/documents/:id/branded-docz` → `body_dto_b64`,
 * decoded to `bodyDto`). Those bytes are the docz `encodeBlockList` output over
 * the EXACT body blocks the branded PDF lays out (raw_html already exploded to
 * typed tables/lists server-side), so the app frames NO body bytes itself
 * (RULE ZERO) — it only prepends the page geometry. Feed the result to
 * `PageLayoutModule.layout(...)`.
 */
export function encodeBrandedBodyInput(bodyDto: Uint8Array): Uint8Array {
  return encodePlInput(bodyDto, BODY_PAGE_CONFIG);
}

/**
 * Project server docz transport blocks onto the docz block-DTO palette — the
 * OFFLINE-mock fallback only (the real preview consumes the server's render-ready
 * block-DTO directly). Heading → a docz heading; every other kind (paragraph /
 * list_item / blockquote / code / opaque `html`) → a paragraph carrying its plain
 * text. The mock has no Zig ingest/explode, so its tables stay as text — the
 * live backend gives full fidelity.
 */
export function blocksToPalette(
  blocks: readonly DoczTransportBlock[],
): PaletteBlock[] {
  return blocks.map((b) => {
    const runs: readonly FragmentRun[] = [{ text: b.text, marks: [] }];
    if (b.kind === "heading") {
      return { kind: "heading", level: b.level ?? 1, runs };
    }
    return { kind: "paragraph", runs };
  });
}

// ── canvas helpers (point-space) ─────────────────────────────────────────────
const PT_PER_IN = 72;
const CSS_PX_PER_IN = 96;

/** The CSS-px scale + backing store DPR for a given zoom — mirrors
 *  web-page-layout's `render.resolve` so the chrome overlay draws in the SAME
 *  device space as `renderSurface`'s body canvases. */
export function pageScale(zoom: number): { cssScale: number; backing: number } {
  const dpr = globalThis.devicePixelRatio || 1;
  return { cssScale: (CSS_PX_PER_IN / PT_PER_IN) * zoom, backing: Math.min(dpr, 2) };
}

/**
 * Size `canvas` to an A4 page at `zoom` and return a 2D context whose transform
 * folds pt → CSS-px → device-px, so the caller draws in POINTS (identical setup
 * to web-page-layout's `renderPage`). Used for the standalone COVER canvas.
 */
export function preparePageCanvas(
  canvas: HTMLCanvasElement,
  zoom: number,
): CanvasRenderingContext2D {
  const { cssScale, backing } = pageScale(zoom);
  const cssW = PAGE_W_PT * cssScale;
  const cssH = PAGE_H_PT * cssScale;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.width = Math.round(cssW * backing);
  canvas.height = Math.round(cssH * backing);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("brandedPreview: 2D context unavailable");
  ctx.setTransform(backing * cssScale, 0, 0, backing * cssScale, 0, 0);
  return ctx;
}

/** Re-acquire a point-space context on an ALREADY-sized page canvas (one painted
 *  by `renderSurface`) so the running header/footer chrome draws over the body in
 *  the same coordinate space. */
export function pointContextFor(
  canvas: HTMLCanvasElement,
  zoom: number,
): CanvasRenderingContext2D | null {
  const { cssScale, backing } = pageScale(zoom);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(backing * cssScale, 0, 0, backing * cssScale, 0, 0);
  return ctx;
}

/** Center-wrap `text` into lines that fit `maxWidthPt` at the ctx's current font.
 *  A tiny greedy wrap for the cover title/subtitle (no hyphenation). */
function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidthPt: number,
): string[] {
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let line = words[0] as string;
  for (let i = 1; i < words.length; i++) {
    const next = `${line} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidthPt) line = next;
    else {
      lines.push(line);
      line = words[i] as string;
    }
  }
  lines.push(line);
  return lines;
}

export interface CoverContent {
  readonly title: string;
  readonly subtitle: string;
  /** The pre-loaded cover wordmark, or null if it failed to load (title still
   *  paints — the cover never blanks on a missing asset). */
  readonly wordmark: HTMLImageElement | null;
}

/**
 * Paint the Cerebr8 COVER page (cerebr8.plate.zon `.sections[0]` + the first-page
 * footer) onto a prepared (point-space) context: the centered wordmark ~⅓ down,
 * the navy `CoverTitle` (26pt bold), the navy `CoverSubtitle` (16pt bold), and
 * the bottom-anchored "Prepared for editorial review" / "CONFIDENTIAL" lines.
 */
export function drawCover(ctx: CanvasRenderingContext2D, cover: CoverContent): void {
  ctx.clearRect(0, 0, PAGE_W_PT, PAGE_H_PT);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, PAGE_W_PT, PAGE_H_PT);
  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  const centerX = PAGE_W_PT / 2;
  const contentW = PAGE_W_PT - MARGIN_LEFT_PT - MARGIN_RIGHT_PT; // 451pt

  // Wordmark: 451×45pt, centered, ~⅓ down (plate `CoverGap` leading 200 from the
  // 99pt top margin). Faithful to the plate's pre-padded full-width canvas.
  const logoW = 451;
  const logoH = 45;
  const logoTop = 280;
  if (cover.wordmark) {
    ctx.drawImage(cover.wordmark, (PAGE_W_PT - logoW) / 2, logoTop, logoW, logoH);
  }

  // CoverTitle — 26pt bold navy, space_before 70 (plate `CoverTitle`).
  let y = logoTop + logoH + 70;
  ctx.fillStyle = CEREBR8_NAVY;
  ctx.font = "700 26px Alexandria";
  for (const line of wrapLines(ctx, cover.title, contentW)) {
    ctx.fillText(line, centerX, y);
    y += 32; // plate CoverTitle leading
  }

  // CoverSubtitle — 16pt bold navy, space_before 6 (plate `CoverSubtitle`).
  y += 6;
  ctx.font = "700 16px Alexandria";
  for (const line of wrapLines(ctx, cover.subtitle, contentW)) {
    ctx.fillText(line, centerX, y);
    y += 22; // plate CoverSubtitle leading
  }

  // First-page footer — bottom-anchored PreparedFor + CONFIDENTIAL (9pt grey).
  ctx.fillStyle = CEREBR8_GREY;
  ctx.font = "400 9px Alexandria";
  const footerBottom = PAGE_H_PT - MARGIN_BOTTOM_PT;
  ctx.fillText("Prepared for editorial review", centerX, footerBottom - 30);
  ctx.fillText("CONFIDENTIAL: Draft, not for publication", centerX, footerBottom - 15);
}

export interface RunningChrome {
  /** 1-based page number shown in the footer (cover is page 1 → body starts 2). */
  readonly pageNumber: number;
  /** Running-header title (the document title), right-aligned. */
  readonly title: string;
  /** The pre-loaded header mark, or null (the title + number still paint). */
  readonly mark: HTMLImageElement | null;
}

/**
 * Paint the per-page running header + footer chrome (cerebr8.plate.zon
 * `.running_elements`) over a body page: the 18×19pt mark at the left margin +
 * the navy `HeaderTitle` (9pt, 0.75 opacity) right-aligned, and the centered
 * `FooterNum` page number (9pt grey). Drawn in the page margins, clear of the
 * body text box (top margin 99pt).
 */
export function drawRunningChrome(ctx: CanvasRenderingContext2D, ch: RunningChrome): void {
  // Header band — sits in the top margin, its baseline above the 99pt body top.
  ctx.save();
  ctx.globalAlpha = 0.75; // plate header box `.opacity = 0.75`
  const markTop = 72;
  const markW = 18;
  const markH = 19;
  if (ch.mark) ctx.drawImage(ch.mark, MARGIN_LEFT_PT, markTop, markW, markH);
  ctx.textBaseline = "middle";
  ctx.textAlign = "right";
  ctx.fillStyle = CEREBR8_NAVY;
  ctx.font = "400 9px Alexandria";
  ctx.fillText(ch.title, PAGE_W_PT - MARGIN_RIGHT_PT, markTop + markH / 2);
  ctx.restore();

  // Footer — centered page number in the bottom margin.
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillStyle = CEREBR8_GREY;
  ctx.font = "400 9px Alexandria";
  ctx.fillText(String(ch.pageNumber), PAGE_W_PT / 2, PAGE_H_PT - MARGIN_BOTTOM_PT + 30);
}

/** Load an image asset for canvas drawing; resolves `null` (never rejects) so a
 *  missing/blocked asset degrades to a text-only cover/header instead of failing
 *  the whole preview. */
export function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (typeof Image === "undefined") return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
